import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/feature/Toast";
import { sendTestEmail, EMAIL_TEMPLATES } from "@/lib/email";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { timeAgo } from "@/pages/admin/dashboard/types";

interface EmailEvent {
  id: string;
  user_id: string | null;
  event_type: string;
  template_code: string | null;
  recipient_email: string;
  subject: string | null;
  status: string;
  resend_email_id: string | null;
  created_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  error_message: string | null;
}

const STATUS_META: Record<string, { label: string; tone: string }> = {
  pending: { label: "Pendiente", tone: "bg-background-100 text-foreground-600" },
  sent: { label: "Enviado", tone: "bg-secondary-100 text-secondary-700" },
  delivered: { label: "Entregado", tone: "bg-primary-100 text-primary-700" },
  opened: { label: "Abierto", tone: "bg-primary-100 text-primary-700" },
  clicked: { label: "Click", tone: "bg-primary-100 text-primary-700" },
  delayed: { label: "Retrasado", tone: "bg-background-100 text-foreground-600" },
  bounced: { label: "Rebotado", tone: "bg-accent-100 text-accent-700" },
  complained: { label: "Queja", tone: "bg-accent-100 text-accent-700" },
  failed: { label: "Fallido", tone: "bg-accent-100 text-accent-700" },
};

function formatError(raw: string | null): string {
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.message) return parsed.message;
    if (parsed?.name) return parsed.name;
    return raw;
  } catch {
    return raw;
  }
}

export default function EmailsTab() {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTest, setSelectedTest] = useState("WELCOME");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCode, setFilterCode] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("email_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(300);
      if (err) throw err;
      setEvents((data ?? []) as EmailEvent[]);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "No se pudieron cargar los emails."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((ev) => {
      if (filterCode && ev.template_code !== filterCode && ev.event_type !== filterCode) {
        return false;
      }
      if (!q) return true;
      return (
        ev.recipient_email.toLowerCase().includes(q) ||
        (ev.template_code ?? "").toLowerCase().includes(q) ||
        (ev.event_type ?? "").toLowerCase().includes(q) ||
        (ev.subject ?? "").toLowerCase().includes(q)
      );
    });
  }, [events, search, filterCode]);

  async function handleSendTest() {
    if (!testEmail.trim() && !profile?.email) {
      showToast("Ingresá un email de prueba válido.", "error");
      return;
    }
    setSendingTest(true);
    const result = await sendTestEmail(selectedTest, testEmail.trim() || undefined);
    setSendingTest(false);
    if (result.error) {
      showToast(result.error, "error");
      return;
    }
    if (result.skipped) {
      showToast("El email fue omitido (ya enviado o preferencia desactivada).", "info");
    } else {
      showToast(`Email de prueba enviado a ${testEmail.trim() || profile?.email}.`, "success");
    }
    fetchEvents();
  }

  const totalSent = events.filter((e) =>
    ["sent", "delivered", "opened", "clicked"].includes(e.status)
  ).length;
  const totalFailed = events.filter((e) =>
    ["failed", "bounced", "complained"].includes(e.status)
  ).length;
  const totalOpened = events.filter((e) =>
    ["opened", "clicked"].includes(e.status)
  ).length;

  return (
    <div className="p-5 md:p-6">
      {/* Encabezado + prueba */}
      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-base font-bold text-foreground-950">
            Correos transaccionales
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            Historial de emails enviados a través de Resend, con estado y
            eventos de entrega.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <div className="rounded-lg border border-background-200/70 bg-background-50 px-4 py-2.5">
              <p className="text-xs text-foreground-500">Enviados</p>
              <p className="text-lg font-bold text-secondary-700">{totalSent}</p>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 px-4 py-2.5">
              <p className="text-xs text-foreground-500">Abiertos</p>
              <p className="text-lg font-bold text-primary-700">{totalOpened}</p>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 px-4 py-2.5">
              <p className="text-xs text-foreground-500">Fallidos</p>
              <p className="text-lg font-bold text-accent-700">{totalFailed}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-4">
          <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-900">
            <i className="ri-send-plane-line text-base text-primary-500"></i>
            Prueba de correo
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            Envía una plantilla de prueba a cualquier dirección de email.
          </p>
          <label className="mb-1.5 mt-3 block text-xs font-semibold text-foreground-700">
            Plantilla
          </label>
          <select
            value={selectedTest}
            onChange={(e) => setSelectedTest(e.target.value)}
            className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
          >
            {EMAIL_TEMPLATES.map((t) => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </select>
          <label className="mb-1.5 mt-3 block text-xs font-semibold text-foreground-700">
            Email de prueba
          </label>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder={profile?.email ?? "tucorreo@ejemplo.com"}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
          <button
            type="button"
            onClick={handleSendTest}
            disabled={sendingTest}
            className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {sendingTest ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Enviando…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                Enviar prueba
              </>
            )}
          </button>
        </div>
      </div>

      {/* Búsqueda y filtro */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por destinatario, plantilla o asunto…"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <select
          value={filterCode}
          onChange={(e) => setFilterCode(e.target.value)}
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
        >
          <option value="">Todas las plantillas</option>
          {EMAIL_TEMPLATES.map((t) => (
            <option key={t.code} value={t.code}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center py-16 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={fetchEvents}
            className="mt-3 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-mail-line"
          title="Sin emails"
          description={
            search || filterCode
              ? "No hay emails que coincidan con la búsqueda."
              : "Aún no se ha enviado ningún correo desde este panel."
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/60 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-4 py-3 font-semibold">Destinatario</th>
                <th className="px-4 py-3 font-semibold">Plantilla</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-4 py-3 font-semibold">Fecha</th>
                <th className="px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/60">
              {filtered.map((ev) => {
                const meta = STATUS_META[ev.status] ?? STATUS_META.pending;
                const isExpanded = expandedId === ev.id;
                const errMsg = formatError(ev.error_message);
                return (
                  <>
                    <tr
                      key={ev.id}
                      className="cursor-pointer hover:bg-background-100/40"
                      onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                    >
                      <td className="max-w-[220px] truncate px-4 py-3 text-foreground-800">
                        {ev.recipient_email}
                      </td>
                      <td className="px-4 py-3">
                        <span className="whitespace-nowrap rounded-full bg-background-100 px-2 py-0.5 text-xs font-medium text-foreground-700">
                          {ev.template_code ?? ev.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-foreground-500">
                        {timeAgo(ev.created_at)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-foreground-400">
                        <i
                          className={`ri-arrow-down-s-line transition-transform ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        ></i>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${ev.id}-detail`} className="bg-background-100/40">
                        <td colSpan={5} className="px-4 py-4">
                          <div className="grid gap-2 text-sm sm:grid-cols-2">
                            <div>
                              <p className="text-xs font-semibold text-foreground-500">Asunto</p>
                              <p className="text-foreground-800">{ev.subject ?? "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-foreground-500">Resend ID</p>
                              <p className="break-all text-foreground-800">
                                {ev.resend_email_id ?? "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-foreground-500">Evento</p>
                              <p className="text-foreground-800">{ev.event_type}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-foreground-500">Entregado</p>
                              <p className="text-foreground-800">
                                {ev.delivered_at ? new Date(ev.delivered_at).toLocaleString() : "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-foreground-500">Abierto</p>
                              <p className="text-foreground-800">
                                {ev.opened_at ? new Date(ev.opened_at).toLocaleString() : "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-foreground-500">Click</p>
                              <p className="text-foreground-800">
                                {ev.clicked_at ? new Date(ev.clicked_at).toLocaleString() : "—"}
                              </p>
                            </div>
                            {errMsg && (
                              <div className="sm:col-span-2">
                                <p className="text-xs font-semibold text-accent-600">Error</p>
                                <p className="break-all text-accent-700">{errMsg}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}